import React, { Fragment, useCallback } from 'react';

import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import { trackGA4Event, trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import { getLinkHostname } from '@gumtree/shared/src/util/ga4-bff';
import DesktopAppStoreIcon from '@gumtree/ui-library/src/icon/svg/footer/app-store.svg';
import DesktopFacebookIcon from '@gumtree/ui-library/src/icon/svg/footer/facebook.svg';
import DesktopGooglePlayIcon from '@gumtree/ui-library/src/icon/svg/footer/google-play.svg';
import DesktopInstagramIcon from '@gumtree/ui-library/src/icon/svg/footer/instagram.svg';
import DesktopPinterestIcon from '@gumtree/ui-library/src/icon/svg/footer/pinterest.svg';
import DesktopTiktokIcon from '@gumtree/ui-library/src/icon/svg/footer/tiktok.svg';
import DesktopXIcon from '@gumtree/ui-library/src/icon/svg/footer/x.svg';
import DesktopYoutubeIcon from '@gumtree/ui-library/src/icon/svg/footer/youtube.svg';

import type { DeviceMeta } from '../reducers/common/base-config';
import AppStoreBadge from './app-store-badge.svg';
import GooglePlayBadge from './google-play-badge.svg';
import FacebookIcon from './social-icons/facebook-logo.svg';
import InstagramIcon from './social-icons/instagram-logo.svg';
import TiktokIcon from './social-icons/tiktok-logo.svg';
import TwitterIcon from './social-icons/twitter-logo-new.svg';
import YoutubeIcon from './social-icons/youtube-logo.svg';
import PinterestIcon from './social-icons/pinterest-logo.svg';
import { androidNames, iOSNames } from './footer-app-links';
import { trackSocialFooterClick } from './footer-social';
import {
    ModernFooterAppLinks,
    ModernFooterBlock,
    ModernFooterCardContent,
    ModernFooterCardTitle,
    ModernFooterChevron,
    ModernFooterContent,
    ModernFooterCopyright,
    ModernFooterCopyrightText,
    ModernFooterDetails,
    ModernFooterGroup,
    ModernFooterGroupTitle,
    ModernFooterNav,
    ModernFooterSectionCards,
    ModernFooterSocialLink,
    ModernFooterSocialLinks,
    ModernFooterSummary,
    VotFooterAppLinks,
    VotFooterApps,
    VotFooterBlock,
    VotFooterChevron,
    VotFooterCommunity,
    VotFooterCommunityTitle,
    VotFooterCopyright,
    VotFooterCopyrightLinks,
    VotFooterDetails,
    VotFooterDesktopDivider,
    VotFooterDesktopAppBadge,
    VotFooterDesktopAppBadgeText,
    VotFooterGroupTitle,
    VotFooterNav,
    VotFooterPartnerLink,
    VotFooterPartners,
    VotFooterPrimary,
    VotFooterSectionCards,
    VotFooterSecondary,
    VotFooterSocialLink,
    VotFooterSocialLinks,
    VotFooterSummary,
    VotFooterTitle,
    modernFooterBadgeLinkStyle,
    modernFooterCardLinkStyle,
    modernFooterTextLinkStyle,
    getVotFooterBadgeLinkStyle,
    votFooterBadgeLinkStyle,
    getVotFooterTextLinkStyle,
} from './modern-footer-style';

const mobileNames = ([] as string[]).concat(iOSNames, androidNames);

const socialLinks = [
    {
        href: 'https://www.facebook.com/gumtree',
        label: 'Facebook',
        Icon: FacebookIcon,
    },
    {
        href: 'https://www.instagram.com/gumtreeuk/',
        label: 'Instagram',
        Icon: InstagramIcon,
    },
    {
        href: 'https://www.tiktok.com/@gumtreeuk',
        label: 'Tiktok',
        Icon: TiktokIcon,
    },
    {
        href: 'https://twitter.com/Gumtree',
        label: 'Twitter',
        Icon: TwitterIcon,
    },
    {
        href: 'https://www.youtube.com/user/GumtreeClassifieds',
        label: 'Youtube',
        Icon: YoutubeIcon,
    },
    {
        href: 'https://www.pinterest.co.uk/gumtreeuk/',
        label: 'Pinterest',
        Icon: PinterestIcon,
    },
];

const votDesktopSocialLinks = [
    { ...socialLinks[1], Icon: DesktopInstagramIcon },
    { ...socialLinks[2], Icon: DesktopTiktokIcon },
    { ...socialLinks[4], Icon: DesktopYoutubeIcon },
    { ...socialLinks[5], Icon: DesktopPinterestIcon },
    { ...socialLinks[3], Icon: DesktopXIcon },
    { ...socialLinks[0], Icon: DesktopFacebookIcon },
];

const VotDesktopAppBadge: React.FC<{ image?: string }> = ({ image }) => {
    const isAppStore = image === 'app-store-badge';
    const isGooglePlay = image === 'google-play-badge';

    if (!isAppStore && !isGooglePlay) {
        return null;
    }

    return (
        <VotFooterDesktopAppBadge
            $app={isAppStore ? 'app-store' : 'google-play'}
            aria-hidden="true"
            data-testid={`vot-footer-desktop-${isAppStore ? 'app-store' : 'google-play'}-badge`}
        >
            {isAppStore ? <DesktopAppStoreIcon /> : <DesktopGooglePlayIcon />}
            <VotFooterDesktopAppBadgeText>
                <span>{isAppStore ? 'Download on the' : 'Get it on'}</span>
                <strong>{isAppStore ? 'APP Store' : 'Google Play'}</strong>
            </VotFooterDesktopAppBadgeText>
        </VotFooterDesktopAppBadge>
    );
};

const getBadge = (image?: string, isVotDesktop = false) => {
    if (isVotDesktop && (image === 'app-store-badge' || image === 'google-play-badge')) {
        return <VotDesktopAppBadge image={image} />;
    }

    switch (image) {
        case 'app-store-badge':
            return <AppStoreBadge />;
        case 'google-play-badge':
            return <GooglePlayBadge />;
        default:
            return null;
    }
};

const ModernFooterLink: React.FC<ModernFooterLinkProps> = ({
    image = '',
    label = '',
    link = '',
    name = '',
    isVotDesktop = false,
    variant = 'text',
}) => {
    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();

            if (mobileNames.includes(name)) {
                trackGA4Event<GA4.ClickFooter>({
                    event: 'click_footer',
                    linkDomain: getLinkHostname(link),
                    linkHeader: 'footer',
                    linkText: label,
                    linkType: 'app link',
                    linkURL: link,
                });
                window.open(link, '_blank');
                return;
            }

            trackGA4PreNav<GA4.ClickFooter>({
                event: 'click_footer',
                linkDomain: getLinkHostname(link),
                linkHeader: 'footer',
                linkText: label,
                linkType: 'text',
                linkURL: link,
            });
            window.location.href = link;
        },
        [label, link, name]
    );

    const badge = getBadge(image, isVotDesktop && variant === 'vot-badge');

    return (
        <a
            css={
                badge
                    ? variant === 'vot-badge'
                        ? isVotDesktop
                            ? getVotFooterBadgeLinkStyle(true)
                            : votFooterBadgeLinkStyle
                        : modernFooterBadgeLinkStyle
                    : variant === 'card'
                    ? modernFooterCardLinkStyle
                    : variant === 'vot-text'
                    ? getVotFooterTextLinkStyle(isVotDesktop)
                    : modernFooterTextLinkStyle
            }
            href={link}
            onClick={handleClick}
            data-testid="modern-footer-link"
            {...qaAttribute('footer-links-item')}
        >
            {badge ? (
                <>
                    <span className="hide-visually">{label}</span>
                    {badge}
                </>
            ) : (
                label
            )}
        </a>
    );
};

const ModernFooterCopyrightLinks: React.FC<{ links: FooterCopyrightLink[] }> = ({ links }) => (
    <>
        <a
            id="utiq__modal-manager"
            href="#manage-utiq"
            className="footer-copyright-link"
            onClick={(e) => {
                e.preventDefault();
                (window as any).Utiq?.queue?.push(() =>
                    (window as any).Utiq?.API.showConsentModal()
                );
            }}
        >
            Manage Utiq
        </a>
        {', '}
        <a
            href={links[0]?.link}
            className="footer-copyright-link"
            {...qaAttribute(`footer-copyright-link-${links[0]?.name}`)}
        >
            {links[0]?.label}
        </a>
        {', '}
        <a
            href={links[1]?.link}
            className="footer-copyright-link"
            {...qaAttribute(`footer-copyright-link-${links[1]?.name}`)}
        >
            {links[1]?.label}
        </a>
        {', '}
        <a
            href="#privacy-settings"
            className="ot-sdk-show-settings footer-copyright-link"
            {...({ name: 'privacy-settings' } as { name: string })}
        >
            Privacy Settings
        </a>
        <span>, </span>
        {' & '}
        <a
            href={links[2]?.link}
            className="footer-copyright-link"
            {...qaAttribute(`footer-copyright-link-${links[2]?.name}`)}
        >
            {links[2]?.label}
        </a>
    </>
);

const VotFooter: React.FC<VotFooterProps> = ({
    apps,
    copyrightLinks,
    device,
    isMotors,
    partners,
    sections,
}) => {
    const isDesktop = device.type === 'desktop';
    const appNames: readonly string[] =
        device.type === 'mobile'
            ? device.os === 'IOS'
                ? iOSNames
                : device.os === 'ANDROID'
                ? androidNames
                : []
            : [...iOSNames, ...androidNames];
    const yearNow = new Date().getFullYear();
    const motorsPartner = partners.find((partner) => partner.id === 'motors');

    return (
        <VotFooterBlock
            $isDesktop={isDesktop}
            {...qaAttribute('footer-container')}
            data-testid="vot-footer"
        >
            <VotFooterNav $isDesktop={isDesktop} aria-label="Footer">
                <VotFooterPrimary $isDesktop={isDesktop}>
                    <VotFooterSectionCards $isDesktop={isDesktop}>
                        {sections.slice(0, 3).map((section) => (
                            <VotFooterDetails
                                $isDesktop={isDesktop}
                                key={section.title}
                                open={device.type !== 'mobile'}
                            >
                                <VotFooterSummary $isDesktop={isDesktop}>
                                    <VotFooterTitle $isDesktop={isDesktop}>
                                        {section.title}
                                    </VotFooterTitle>
                                    <VotFooterChevron
                                        aria-hidden="true"
                                        className="vot-footer-chevron"
                                    />
                                </VotFooterSummary>
                                <ModernFooterCardContent>
                                    {section.items
                                        ?.filter((item) => item.name !== 'popular-searches')
                                        .map((item) => (
                                            <ModernFooterLink
                                                key={item.name}
                                                image={item.image}
                                                label={item.label}
                                                link={item.link}
                                                name={item.name}
                                                isVotDesktop={isDesktop}
                                                variant="vot-text"
                                            />
                                        ))}
                                </ModernFooterCardContent>
                            </VotFooterDetails>
                        ))}
                    </VotFooterSectionCards>

                    {apps.map((section) => (
                        <VotFooterApps $isDesktop={isDesktop} key={section.title}>
                            {isDesktop ? (
                                <VotFooterTitle $isDesktop>{section.title}</VotFooterTitle>
                            ) : (
                                <VotFooterGroupTitle $isDesktop={isDesktop}>
                                    {section.title}
                                </VotFooterGroupTitle>
                            )}
                            <VotFooterAppLinks>
                                {section.items?.map((item) => (
                                    <Fragment key={item.name}>
                                        {appNames.includes(item.name) && (
                                            <ModernFooterLink
                                                image={item.image}
                                                label={item.label}
                                                link={item.link}
                                                name={item.name}
                                                isVotDesktop={isDesktop}
                                                variant={
                                                    isDesktop && !item.image
                                                        ? 'vot-text'
                                                        : 'vot-badge'
                                                }
                                            />
                                        )}
                                    </Fragment>
                                ))}
                            </VotFooterAppLinks>
                        </VotFooterApps>
                    ))}
                </VotFooterPrimary>

                <VotFooterDesktopDivider $isDesktop={isDesktop} />
                <VotFooterSecondary $isDesktop={isDesktop} hasPartners={isMotors}>
                    <VotFooterCommunity $isDesktop={isDesktop}>
                        <VotFooterCommunityTitle $isDesktop={isDesktop}>
                            Join Gumtree Community
                        </VotFooterCommunityTitle>
                        <VotFooterSocialLinks $isDesktop={isDesktop}>
                            {(isDesktop ? votDesktopSocialLinks : socialLinks).map(
                                ({ href, label, Icon }) => (
                                    <VotFooterSocialLink
                                        key={label}
                                        $isDesktop={isDesktop}
                                        href={href}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        onClick={trackSocialFooterClick}
                                    >
                                        <Icon />
                                        <span hidden>{label}</span>
                                    </VotFooterSocialLink>
                                )
                            )}
                        </VotFooterSocialLinks>
                    </VotFooterCommunity>
                    {isMotors && motorsPartner && (
                        <VotFooterPartners $isDesktop={isDesktop}>
                            <VotFooterGroupTitle $isDesktop={isDesktop}>
                                Gumtree Partners
                            </VotFooterGroupTitle>
                            <VotFooterPartnerLink href={motorsPartner.link}>
                                {motorsPartner.label}
                            </VotFooterPartnerLink>
                        </VotFooterPartners>
                    )}
                </VotFooterSecondary>
                <VotFooterDesktopDivider $isDesktop={isDesktop} />

                <VotFooterCopyright $isDesktop={isDesktop} {...qaAttribute('footer-copyright')}>
                    <ModernFooterCopyrightText data-testid="footer-copyright-text">
                        <span>
                            {`© Copyright 2000-${yearNow} Gumtree.com Limited. All rights reserved.`}
                        </span>
                        <br />
                        <br />
                        <span>
                            Gumtree.com Limited (FRN 560524) is an Appointed Representative of
                            Consumer Credit Compliance Limited who are authorised and regulated by
                            the Financial Conduct Authority (FRN 631736).
                        </span>
                        <br />
                        <br />
                        <span>
                            The permissions of Consumer Credit Compliance Limited as a Principal
                            firm allow Gumtree.com Limited to undertake insurance and credit
                            broking. Gumtree.com Limited acts as a credit broker, not a lender. We
                            will refer you to CarMoney Limited (FRN 674094) for credit, we receive a
                            fixed fee commission up to an agreed number of leads, and additional
                            commission for those above the agreed level. We will refer you to
                            Inspop.com Ltd T/A Confused.com (FRN 310635) for Insurance and we
                            receive a fixed fee commission. You will not pay more as a result of our
                            commission arrangements.
                        </span>
                        <br />
                        <br />
                        <span>
                            Gumtree.com Limited, registered in England and Wales with number
                            03934849, 27 Old Gloucester Street, London, WC1N 3AX, United Kingdom.
                            VAT No. 476 0835 68.
                        </span>
                    </ModernFooterCopyrightText>
                    <VotFooterCopyrightLinks $isDesktop={isDesktop}>
                        <ModernFooterCopyrightLinks links={copyrightLinks} />
                    </VotFooterCopyrightLinks>
                </VotFooterCopyright>
            </VotFooterNav>
        </VotFooterBlock>
    );
};

const ModernFooter: React.FC<ModernFooterProps> = ({
    sections = [],
    apps = [],
    copyrightLinks = [],
    device,
    isMotors = false,
    isVotRedesign = false,
    partners = [],
}) => {
    if (isVotRedesign) {
        return (
            <VotFooter
                apps={apps}
                copyrightLinks={copyrightLinks}
                device={device}
                isMotors={isMotors}
                partners={partners}
                sections={sections}
            />
        );
    }

    const yearNow = new Date().getFullYear();
    const appNames: readonly string[] =
        device.os === 'IOS' ? iOSNames : device.os === 'ANDROID' ? androidNames : [];

    return (
        <ModernFooterBlock {...qaAttribute('footer-container')} data-testid="modern-footer">
            <ModernFooterNav aria-label="Footer">
                <ModernFooterSectionCards>
                    {sections.slice(0, 3).map((section) => (
                        <ModernFooterDetails key={section.title}>
                            <ModernFooterSummary>
                                <ModernFooterCardTitle>{section.title}</ModernFooterCardTitle>
                                <ModernFooterChevron
                                    className="modern-footer-chevron"
                                    aria-hidden="true"
                                />
                            </ModernFooterSummary>
                            <ModernFooterCardContent>
                                {section.items
                                    ?.filter((item) => item.name !== 'popular-searches')
                                    .map((item) => (
                                        <ModernFooterLink
                                            key={item.name}
                                            image={item.image}
                                            label={item.label}
                                            link={item.link}
                                            name={item.name}
                                            variant="card"
                                        />
                                    ))}
                            </ModernFooterCardContent>
                        </ModernFooterDetails>
                    ))}
                </ModernFooterSectionCards>

                <ModernFooterContent>
                    {apps.map((section) => (
                        <ModernFooterGroup key={section.title}>
                            <ModernFooterGroupTitle>{section.title}</ModernFooterGroupTitle>
                            <ModernFooterAppLinks>
                                {section.items?.map((item) => (
                                    <Fragment key={item.name}>
                                        {appNames.includes(item.name) && (
                                            <ModernFooterLink
                                                image={item.image}
                                                label={item.label}
                                                link={item.link}
                                                name={item.name}
                                            />
                                        )}
                                    </Fragment>
                                ))}
                            </ModernFooterAppLinks>
                        </ModernFooterGroup>
                    ))}

                    <ModernFooterGroup>
                        <ModernFooterGroupTitle>Join Gumtree Community</ModernFooterGroupTitle>
                        <ModernFooterSocialLinks>
                            {socialLinks.map(({ href, label, Icon }) => (
                                <ModernFooterSocialLink
                                    key={label}
                                    href={href}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                    onClick={trackSocialFooterClick}
                                >
                                    <Icon />
                                    <span hidden>{label}</span>
                                </ModernFooterSocialLink>
                            ))}
                        </ModernFooterSocialLinks>
                    </ModernFooterGroup>

                    <ModernFooterCopyright {...qaAttribute('footer-copyright')}>
                        <ModernFooterCopyrightText data-testid="footer-copyright-text">
                            {`© Copyright 2000-${yearNow} Gumtree.com Limited. All rights reserved.`}
                            <br />
                            <br />
                            Gumtree.com Limited (FRN 560524) is an Appointed Representative of
                            Consumer Credit Compliance Limited who are authorised and regulated by
                            the Financial Conduct Authority (FRN 631736).
                            <br />
                            <br />
                            The permissions of Consumer Credit Compliance Limited as a Principal
                            firm allow Gumtree.com Limited to undertake insurance and credit
                            broking. Gumtree.com Limited acts as a credit broker, not a lender. We
                            will refer you to CarMoney Limited (FRN 674094) for credit, we receive a
                            fixed fee commission up to an agreed number of leads, and additional
                            commission for those above the agreed level. We will refer you to
                            Inspop.com Ltd T/A Confused.com (FRN 310635) for Insurance and we
                            receive a fixed fee commission. You will not pay more as a result of our
                            commission arrangements.
                            <br />
                            <br />
                            Gumtree.com Limited, registered in England and Wales with number
                            03934849, 27 Old Gloucester Street, London, WC1N 3AX, United Kingdom.
                            VAT No. 476 0835 68.
                        </ModernFooterCopyrightText>
                        <ModernFooterCopyrightLinks links={copyrightLinks} />
                    </ModernFooterCopyright>
                </ModernFooterContent>
            </ModernFooterNav>
        </ModernFooterBlock>
    );
};

interface ModernFooterLinkProps {
    image?: string;
    isVotDesktop?: boolean;
    label?: string;
    link?: string;
    name?: string;
    variant?: 'card' | 'text' | 'vot-badge' | 'vot-text';
}

interface FooterItem {
    image?: string;
    label?: string;
    link?: string;
    name: string;
}

interface FooterSection {
    items?: FooterItem[];
    title: string;
}

interface FooterCopyrightLink {
    label?: string;
    link?: string;
    name?: string;
}

interface FooterPartner {
    id: string;
    label: string;
    link: string;
}

interface VotFooterProps {
    apps: FooterSection[];
    copyrightLinks: FooterCopyrightLink[];
    device: DeviceMeta;
    isMotors: boolean;
    partners: FooterPartner[];
    sections: FooterSection[];
}

interface ModernFooterProps {
    apps: FooterSection[];
    copyrightLinks: FooterCopyrightLink[];
    device: DeviceMeta;
    isMotors?: boolean;
    isVotRedesign?: boolean;
    partners?: FooterPartner[];
    sections: FooterSection[];
}

export default ModernFooter;
